/**
 * CaribbeanLeaf Customer Portal Layout
 *
 * Main layout for B2C customer-facing pages. Uses teal accent
 * to visually differentiate from the coral staff portal.
 *
 * Features:
 * - Responsive header with mobile menu
 * - Shopping cart indicator (when authenticated)
 * - Footer with links
 * - Teal primary accent
 */

import { type ReactNode, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import {
  Leaf,
  Menu,
  X,
  ShoppingCart,
  MapPin,
  User,
  LogOut,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useCustomerAuth } from '@/hooks/useCustomerAuth';
import { customerPath } from '@/config/customer';

// ─────────────────────────────────────────────────────────────────────────────
// Navigation Items
// ─────────────────────────────────────────────────────────────────────────────

const publicNavItems = [
  { to: customerPath('/shop'), label: 'Shop' },
  { to: customerPath('/locations'), label: 'Locations' },
  { to: customerPath('/sacramental'), label: 'Sacramental' },
  { to: customerPath('/about'), label: 'About' },
];

const authNavItems = [
  { to: customerPath('/account'), label: 'My Account' },
  { to: customerPath('/orders'), label: 'My Orders' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

interface CustomerLayoutProps {
  children: ReactNode;
}

export function CustomerLayout({ children }: CustomerLayoutProps) {
  const { customer, isAuthenticated, logout } = useCustomerAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate(customerPath('/'));
    setMobileMenuOpen(false);
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <div className="min-h-screen flex flex-col bg-surface-primary">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-customer focus:text-white focus:rounded">
        Skip to content
      </a>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-surface-secondary border-b border-brutal-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to={customerPath('/')} className="flex items-center gap-2 group">
              <div className="p-1.5 bg-customer/20 rounded border border-customer/30 group-hover:bg-customer/30 transition-colors">
                <Leaf className="h-5 w-5 text-customer" />
              </div>
              <span className="font-bold text-lg text-text-primary">
                CaribbeanLeaf
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              {publicNavItems.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      'text-sm font-medium transition-colors',
                      isActive
                        ? 'text-customer'
                        : 'text-text-secondary hover:text-text-primary'
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            {/* Desktop Actions */}
            <div className="hidden md:flex items-center gap-3">
              {isAuthenticated ? (
                <>
                  {/* Cart */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative text-text-secondary hover:text-customer"
                    onClick={() => navigate(customerPath('/cart'))}
                  >
                    <ShoppingCart className="h-5 w-5" />
                  </Button>

                  {/* Account Dropdown (simplified) */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-text-secondary hover:text-text-primary"
                    onClick={() => navigate(customerPath('/account'))}
                  >
                    <User className="h-4 w-4 mr-2" />
                    {customer?.first_name || 'Account'}
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-text-secondary hover:text-coral"
                    onClick={handleLogout}
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-text-secondary hover:text-text-primary"
                    onClick={() => navigate(customerPath('/login'))}
                  >
                    Sign In
                  </Button>
                  <Button
                    size="sm"
                    className="bg-customer hover:bg-customer-600 text-white"
                    onClick={() => navigate(customerPath('/register'))}
                  >
                    Create Account
                  </Button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden text-text-primary"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-label="Main menu"
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-brutal-border bg-surface-secondary">
            <div className="px-4 py-4 space-y-3">
              {publicNavItems.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={closeMobileMenu}
                  className={({ isActive }) =>
                    cn(
                      'block px-3 py-2 rounded text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-customer/20 text-customer'
                        : 'text-text-secondary hover:bg-surface-tertiary hover:text-text-primary'
                    )
                  }
                >
                  {item.label}
                </NavLink>
              ))}

              {isAuthenticated ? (
                <>
                  <hr className="border-brutal-border my-3" />
                  {authNavItems.map(item => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={closeMobileMenu}
                      className={({ isActive }) =>
                        cn(
                          'block px-3 py-2 rounded text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-customer/20 text-customer'
                            : 'text-text-secondary hover:bg-surface-tertiary hover:text-text-primary'
                        )
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
                  <NavLink
                    to={customerPath('/cart')}
                    onClick={closeMobileMenu}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-customer/20 text-customer'
                          : 'text-text-secondary hover:bg-surface-tertiary hover:text-text-primary'
                      )
                    }
                  >
                    <ShoppingCart className="h-4 w-4" />
                    Cart
                  </NavLink>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 w-full px-3 py-2 rounded text-sm font-medium text-coral hover:bg-coral/10 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <hr className="border-brutal-border my-3" />
                  <NavLink
                    to={customerPath('/login')}
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 rounded text-sm font-medium text-text-secondary hover:bg-surface-tertiary hover:text-text-primary"
                  >
                    Sign In
                  </NavLink>
                  <NavLink
                    to={customerPath('/register')}
                    onClick={closeMobileMenu}
                    className="block px-3 py-2 rounded text-sm font-medium bg-customer text-white hover:bg-customer-600 text-center"
                  >
                    Create Account
                  </NavLink>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main id="main-content" className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-surface-secondary border-t border-brutal-border py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-customer/20 rounded border border-customer/30">
                  <Leaf className="h-5 w-5 text-customer" />
                </div>
                <span className="font-bold text-lg text-text-primary">
                  CaribbeanLeaf
                </span>
              </div>
              <p className="text-sm text-text-tertiary">
                Licensed cannabis dispensary serving the Caribbean with premium
                products and exceptional service.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold text-text-primary mb-4">Shop</h4>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li>
                  <Link to={customerPath('/shop')} className="hover:text-customer">
                    All Products
                  </Link>
                </li>
                <li>
                  <Link
                    to={`${customerPath('/shop')}?category=flower`}
                    className="hover:text-customer"
                  >
                    Flower
                  </Link>
                </li>
                <li>
                  <Link
                    to={`${customerPath('/shop')}?category=edibles`}
                    className="hover:text-customer"
                  >
                    Edibles
                  </Link>
                </li>
                <li>
                  <Link
                    to={`${customerPath('/shop')}?category=concentrates`}
                    className="hover:text-customer"
                  >
                    Concentrates
                  </Link>
                </li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="font-semibold text-text-primary mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-text-secondary">
                <li>
                  <Link to={customerPath('/about')} className="hover:text-customer">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to={customerPath('/locations')} className="hover:text-customer">
                    Store Locations
                  </Link>
                </li>
                <li>
                  <Link to={customerPath('/sacramental')} className="hover:text-customer">
                    Sacramental
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-semibold text-text-primary mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-text-tertiary">
                <li>Privacy Policy</li>
                <li>Terms of Service</li>
              </ul>
              <p className="text-xs text-text-tertiary mt-3">
                Licensed under the Antigua &amp; Barbuda Medicinal Cannabis Authority
              </p>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-brutal-border flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-text-tertiary">
              &copy; {new Date().getFullYear()} CaribbeanLeaf. All rights
              reserved.
            </p>
            <div className="flex items-center gap-2 text-sm text-text-tertiary">
              <MapPin className="h-4 w-4" />
              Licensed in Antigua & Barbuda
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default CustomerLayout;
